/** Typed access to VITE_ env vars. Never put secrets in VITE_ vars. */

const DEFAULT_API_BASE_URL = "http://localhost:8787";
const SURFACE_NAME = "Staff Ops";

export type RawApiEnv = {
  VITE_API_BASE_URL?: string;
  VITE_API_MOCK?: string;
  VITE_STAFF_VENUE_ID?: string;
  DEV?: boolean;
};

// ASSUMPTION (FR-STAFF-001/003): MVP staff login has no venue-selection UI —
// a device/deployment is provisioned for exactly one venue, so the venue id
// staff authenticate against is a build-time env value, not client-asserted
// identity (the server still resolves staff identity/role/branch from the
// verified PIN, never from this id alone). Defaults to the local seed
// fixture venue for dev/demo. Revisit if/when multi-venue devices ship.
const DEFAULT_STAFF_VENUE_ID = "11111111-1111-1111-1111-111111111111";

export type ApiMode =
  | { kind: "backend"; apiBaseUrl: string }
  | { kind: "mock"; apiBaseUrl: string; reason: "explicit-local-demo" };

export class BackendApiUnavailableError extends Error {
  readonly code = "BACKEND_API_UNAVAILABLE";
  readonly recoverable = true;
  readonly requiredEnv = "VITE_API_BASE_URL";
  readonly demoOverride = "VITE_API_MOCK=1";

  constructor(surfaceName: string) {
    super(
      `${surfaceName} backend API is unavailable; set VITE_API_BASE_URL or enable explicit VITE_API_MOCK=1 for local demo mode.`,
    );
    this.name = "BackendApiUnavailableError";
  }
}

/** Resolve whether this frontend must use backend mode or explicit local mock mode. */
export function resolveApiMode(raw: RawApiEnv): ApiMode {
  const apiBaseUrl = raw.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL;
  if (raw.VITE_API_MOCK === "1") {
    return { kind: "mock", apiBaseUrl, reason: "explicit-local-demo" };
  }
  return { kind: "backend", apiBaseUrl };
}

/** Backend mode must fail visibly instead of silently falling back to mocks. */
export function assertBackendModeReady(mode: ApiMode, surfaceName = SURFACE_NAME): void {
  if (mode.kind === "backend" && mode.apiBaseUrl.trim().length === 0) {
    throw new BackendApiUnavailableError(surfaceName);
  }
}

export const apiMode = resolveApiMode(import.meta.env);
assertBackendModeReady(apiMode);

export const env = {
  apiBaseUrl: apiMode.apiBaseUrl,
  apiMock: apiMode.kind === "mock",
  apiMode: apiMode.kind,
  isDev: import.meta.env.DEV,
  staffVenueId: import.meta.env.VITE_STAFF_VENUE_ID ?? DEFAULT_STAFF_VENUE_ID,
} as const;
